import { AfterViewInit, Component, ElementRef, EventEmitter, HostBinding, HostListener, Input, Output, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { BsDropdownConfig } from 'ngx-bootstrap/dropdown';
import { ToggleItem } from '../../models/toggle-item';
import { isUndefined } from '../../models/utils';
import { ContextMenuComponent } from '../context-menu/context-menu.component';

@Component({
    standalone: false,
    selector: 'res-collapsible-toggle-box-group',
    templateUrl: './collapsible-toggle-box-group.component.html',
    styleUrls: ['./collapsible-toggle-box-group.component.scss'],
    providers: [BsDropdownConfig]
})
export class CollapsibleToggleBoxGroupComponent implements AfterViewInit, OnChanges {
    @HostBinding('class') classes = 'maximized-component';
    @Input() toggleItems: Array<ToggleItem>;
    @Input() automationId = 'collapsible-toggle-box-group';
    @Input() displayAlwaysOnRight = true;
    @Output() onToggleValueChanged: EventEmitter<any> = new EventEmitter();
    @ViewChild('collapsedContextMenu') collapsedContextMenu: ContextMenuComponent;
    // set this for components that use this control and can be hidden, true - when visible, false - when hidden
    @Input() activated = true;
    @Input() isSkeuomorphic = false;
    @Input() hasDeselectAll = false;
    @Input() labelPaddingLeft: number;
    @Input() labelPaddingRight: number;
    @Input() removeLeftMargin = false;
    @Input() buttonTooltip = 'More options';
    @Input() isAdvancedSearch = false;

    toggleElements: Array<HTMLElement>;
    toggleItemsWidth = 0;
    dropdownToggleButtonVisible = false;
    openMenuOnLeft = !this.displayAlwaysOnRight;

    @HostListener('window:resize', ['$event'])
    onResize(event) {
        this.checkOverflowWidth();
    }

    @HostListener('window:resizerResize', ['$event'])
    onResizerResize(event) {
        this.checkOverflowWidth();
    }

    constructor(private elemRef: ElementRef, dropDownConfig: BsDropdownConfig) {
        dropDownConfig.autoClose = false;
    }

    ngAfterViewInit(): void {
        if (this.toggleItems && this.elemRef.nativeElement.children[0].children) {
            this.toggleElements = <Array<HTMLElement>>Array.from(this.elemRef.nativeElement.children[0].children);
            this.checkOverflowWidth();
        }
    }

    // for controls that are first hidden, calculate the width only when is first time visible, check overflow each time is visible
    ngOnChanges(changes: SimpleChanges): void {
        if (changes && changes.activated && this.activated) {
            this.checkOverflowWidth();
        }
    }

    deselectAll(): void {
        this.toggleItems?.forEach(toggle => toggle.isChecked = false);
        this.onToggleValueChanged.emit();
    }

    calculateWidth(): void {
        if (this.toggleItemsWidth === 0 && this.toggleItems && this.toggleItems.length > 0 && this.toggleElements && this.toggleElements.length > 0) {
            for (let i = 0; i < this.toggleItems.length; i++) {
                if (!isUndefined(this.toggleElements[i])) {
                    if (!this.toggleElements[i].clientWidth) {
                        this.toggleItemsWidth = 0;
                        return;
                    }
                    this.toggleItems[i].width = this.toggleElements[i].clientWidth;
                    this.toggleItemsWidth += this.toggleItems[i].width;
                } else {
                    this.toggleItemsWidth = 0;
                    return;
                }
            }
        }
    }

    checkOverflowWidth(): void {
        setTimeout(() => {
            if (this.activated && this.toggleItems && this.toggleItems.length > 0) {
                this.calculateWidth();

                if (this.toggleItemsWidth > 0) {
                    this.collapsedContextMenu.closeAllContextMenus();

                    const elementWidth = this.elemRef.nativeElement.clientWidth;
                    let calcWidth = 30;
                    for (let i = 0; i < this.toggleItems.length; i++) {
                        this.toggleItems[i].isCollapsed = true;
                        calcWidth += this.toggleItems[i].width;
                        if (i === this.toggleItems.length - 1) {
                            calcWidth -= 30;
                        }
                        if (elementWidth > calcWidth) {
                            this.toggleItems[i].isCollapsed = false;
                        }
                    }
                    this.dropdownToggleButtonVisible = this.collapsedToggleItems().length > 0;
                    // calculate remaining space, check if collapsed toggles fit in this space, if not set context menu to be opened on left
                    if (!this.displayAlwaysOnRight && this.dropdownToggleButtonVisible) {
                        const parentElem = this.elemRef.nativeElement.parentElement;
                        const leftSpace = this.elemRef.nativeElement.offsetLeft - parentElem.offsetLeft;
                        let calcVisibleWidth = 0;
                        this.toggleItems.forEach(t => { if (!t.isCollapsed) { calcVisibleWidth = calcVisibleWidth + t.width; } });
                        const remainingSpace = parentElem.clientWidth - leftSpace - calcVisibleWidth;
                        if (remainingSpace < this.getBiggestWidth()) {
                            this.openMenuOnLeft = true;
                        } else {
                            this.openMenuOnLeft = false;
                        }
                    }
                }
            }
        });
    }

    getBiggestWidth(): number {
        const arrayCollapsed = this.collapsedToggleItems();
        if (arrayCollapsed.length > 0) {
            const sortedList = arrayCollapsed.sort((a, b) => {
                if (a.width > b.width) { return 1; }
                if (a.width < b.width) { return -1; }
                if (a.width === b.width) { return 0; }
            });
            return sortedList.pop().width;
        }
        return 0;
    }

    collapsedToggleItems(): Array<ToggleItem> {
        if (this.toggleItems) {
            return this.toggleItems.filter(t => t.isCollapsed);
        }
        return [];
    }

    // open or close context menu
    onContextMenu(event: MouseEvent): void {
        if (event && this.collapsedContextMenu) {
            event.preventDefault();
            event.stopPropagation();
            let eventTarget = event.target as HTMLElement;
            eventTarget = eventTarget?.nodeName === 'IMG' ? eventTarget.parentElement : eventTarget;
            const parentElCoordinates = {
                x: eventTarget.getBoundingClientRect().left,
                y: eventTarget.getBoundingClientRect().top + document.getElementsByClassName('expand-button')[0].getBoundingClientRect().height - 1
            }
            const node = {
                contextMenuOptions: this.collapsedToggleItems(),
                isCollapsibleContextMenu: true,
                parentElCoordinates: parentElCoordinates
            }
            this.collapsedContextMenu.open(event, node);
        }
    }

    hasCheckedToggles(): boolean {
        if (this.collapsedToggleItems().length > 0) {
            const firstIdx = this.collapsedToggleItems().findIndex(t => t.isChecked);
            return firstIdx !== -1;
        }
        return false;
    }

    onModelChange(changedToggle: ToggleItem): void {
        this.onToggleValueChanged.emit(changedToggle);
    }
}
